/**
 * alljourneys-catalog — the tenant's own services, locations and staff for the
 * All Journeys preview.
 *
 * The preview used to run on four baked Lakeview arrays, so every tenant saw
 * "Academic Advising" at "Fairmount Hall" and the "N services are live today"
 * hint counted a university nobody was looking at. This module reads the same
 * choke points the Services, Locations and Staff surfaces use and maps each row
 * into the shape the preview already speaks (id/name/desc/dur/locs, id/name/addr/tz,
 * id/name), so the preview describes the tenant.
 *
 * Every read is best-effort: a refusal or an empty catalog returns [] and the
 * caller keeps whatever it had. The preview is a picture of the journey, not a
 * report — it must never fail to draw because a list op was slow.
 */
import { listLocations, listStaff, listStudioServices, listQuestionGroups, listQuestions } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";

export interface PreviewService {
  id: string; name: string; desc: string; cat: string;
  dur: string; price: string; window: number; locs: string[];
  /** Booking questions resolved by the read (seed review step 06); [] on an older worker. */
  questions: Record<string, unknown>[];
}
export interface PreviewLocation {
  id: string; name: string; addr: string; dist: string; tz: string;
  next: string; hours: string; phone: string;
}
export interface PreviewStaff { id: string; name: string; }
export interface PreviewDuration { id: string; label: string; sub: string; }

export interface PreviewCatalog {
  services: PreviewService[];
  locations: PreviewLocation[];
  staff: PreviewStaff[];
  durations: PreviewDuration[];
}

const rows = (r: unknown, ...keys: string[]): Record<string, unknown>[] => {
  const o = (r || {}) as Record<string, unknown>;
  for (const k of keys) if (Array.isArray(o[k])) return o[k] as Record<string, unknown>[];
  return Array.isArray(r) ? (r as Record<string, unknown>[]) : [];
};
const str = (v: unknown, fallback = "") => (typeof v === "string" && v ? v : fallback);
const num = (v: unknown, fallback = 0) => (typeof v === "number" && isFinite(v) ? v : fallback);

/** "30 min" / "1 hr 30 min" — the label the preview's service rows print. */
function durLabel(mins: number): string {
  if (!mins) return "";
  const h = Math.floor(mins / 60), m = mins % 60;
  if (!h) return m + " min";
  return h + " hr" + (m ? " " + m + " min" : "");
}

/** Money the way a booking page shows it: the symbol when we know it, "Free" at zero. */
function priceLabel(major: number, currency: string): string {
  if (!major) return "Free";
  const sym = currency === "USD" ? "$" : currency === "GBP" ? "\u00a3" : currency === "EUR" ? "\u20ac" : "";
  const n = major.toFixed(2);
  return sym ? sym + n : n + " " + currency;
}

/** IANA zone → the six-zone vocabulary the preview's timezone control uses. */
function zoneKey(tz: string): string {
  const t = tz.toLowerCase();
  if (t.indexOf("anchorage") > -1) return "alaska";
  if (t.indexOf("honolulu") > -1) return "hawaii";
  if (t.indexOf("los_angeles") > -1 || t.indexOf("vancouver") > -1) return "pacific";
  if (t.indexOf("denver") > -1 || t.indexOf("phoenix") > -1) return "mountain";
  if (t.indexOf("chicago") > -1 || t.indexOf("winnipeg") > -1) return "central";
  return "eastern";
}

function addressOf(l: Record<string, unknown>): string {
  const direct = str(l.address) || str(l.fullAddress) || str(l.addressLine1);
  if (direct) {
    const city = str(l.city), post = str(l.postcode) || str(l.postalCode) || str(l.zip);
    return [direct, city, post].filter(Boolean).join(", ");
  }
  const a = l.address as Record<string, unknown> | undefined;
  if (a && typeof a === "object") {
    return [str(a.line1), str(a.city), str(a.postcode) || str(a.postalCode)].filter(Boolean).join(", ");
  }
  return "";
}

export function loadPreviewCatalog(signal?: AbortSignal): Promise<PreviewCatalog> {
  const nothing = { services: [], locations: [], staff: [], durations: [] };
  const svcP = typeof listStudioServices === "function"
    ? (listStudioServices as (q: unknown, o: unknown) => Promise<unknown>)({ limit: 200 }, { ...LOCATIONS_OPTS, signal }).catch(() => null)
    : Promise.resolve(null);
  const locP = typeof listLocations === "function"
    ? listLocations({ limit: 200 } as never, { ...LOCATIONS_OPTS, signal } as never).catch(() => null)
    : Promise.resolve(null);
  const staffP = typeof listStaff === "function"
    ? (listStaff as (q?: unknown) => Promise<unknown>)({ limit: 200 }).catch(() => null)
    : Promise.resolve(null);
  // The forms creator's groups and questions. StudioServiceDto keeps only the
  // group ids (`questions: string[]`) — its zod parse strips any resolved list —
  // so the resolution happens here, from the two reads the tab already serves.
  const groupsP = typeof listQuestionGroups === "function" ? (listQuestionGroups as (q?: unknown) => Promise<unknown>)({ limit: 200 }).catch(() => null) : Promise.resolve(null);
  const questionsP = typeof listQuestions === "function" ? (listQuestions as (q?: unknown) => Promise<unknown>)({ limit: 500 }).catch(() => null) : Promise.resolve(null);

  return Promise.all([svcP, locP, staffP, groupsP, questionsP]).then(([sr, lr, tr, gr, qr]) => {
    const groups = rows(gr, "data", "groups", "items");
    const questionById: Record<string, Record<string, unknown>> = {};
    rows(qr, "data", "questions", "items").forEach((q) => { const id = str(q.id); if (id) questionById[id] = q; });
    const resolveGroup = (groupId: string): Record<string, unknown>[] => {
      const g = groups.find((x) => str(x.id) === groupId);
      if (!g) return [];
      const items = Array.isArray(g.items) ? (g.items as Record<string, unknown>[]) : [];
      return items.map((it) => {
        const q = questionById[str(it.qid) || str(it.questionId)];
        if (!q || str(q.status) === "archived") return null;
        return { id: str(q.id), label: str(q.label), type: str(q.type), help: str(q.help), required: it.required === true || q.required === true, options: Array.isArray(q.options) ? q.options : [], groupId: str(g.id), groupName: str(g.name) };
      }).filter(Boolean) as Record<string, unknown>[];
    };
    const locations: PreviewLocation[] = rows(lr, "locations", "data", "items").map((l) => ({
      id: str(l.id) || str(l.locationId),
      name: str(l.name) || str(l.locationName, "Location"),
      addr: addressOf(l),
      dist: "",
      tz: zoneKey(str(l.timeZone) || str(l.timezone) || str(l.tz)),
      next: "",
      hours: str(l.openingHoursSummary) || str(l.hours),
      phone: str(l.phone) || str(l.telephone),
    })).filter((l) => !!l.id);

    const allLocIds = locations.map((l) => l.id);
    const services: PreviewService[] = rows(sr, "services", "items", "data")
      .filter((s) => str(s.status, "live") !== "archived" && s.disabled !== true)
      .map((s) => {
        const scope = Array.isArray(s.locScope) ? (s.locScope as unknown[]).map((x) => String(x)) : [];
        return {
          id: str(s.serviceId) || str(s.id),
          name: str(s.name, "Service"),
          desc: str(s.description),
          // The catalogue's category is a machine key (the intent id); its
          // human name rides alongside as `categoryName`. The label is what the
          // service step groups by and prints on its filter chips, so prefer it
          // and fall back to the key only when the row carries no name.
          cat: str(s.categoryName) || str(s.category, "general"),
          dur: str(s.listedDuration) || durLabel(num(s.durationMinutes) || num(s.duration)),
          price: priceLabel(num(s.price), str(s.currency, "USD")),
          // The catalog has no per-service booking window; the preview's own
          // default stands rather than a number invented here.
          window: 60,
          locs: scope.length ? scope : allLocIds,
          // The appointments group is the first id the DTO lists (engine order: appointments · queueing · events).
          questions: Array.isArray(s.bookingQuestions) && (s.bookingQuestions as unknown[]).length
            ? (s.bookingQuestions as Record<string, unknown>[])
            : resolveGroup(Array.isArray(s.questions) && typeof (s.questions as unknown[])[0] === "string" ? String((s.questions as unknown[])[0]) : ""),
        };
      })
      .filter((s) => !!s.id);

    const staff: PreviewStaff[] = rows(tr, "data", "staff", "members", "items").map((m) => ({
      id: str(m.id) || str(m.staffId),
      name: str(m.name) || [str(m.firstName), str(m.lastName)].filter(Boolean).join(" ") || "Team member",
    })).filter((m) => !!m.id);

    // A multi-duration service offers the catalog's own distinct lengths, so the
    // duration step shows what this tenant actually sells.
    const mins = Array.from(new Set(rows(sr, "services", "items", "data")
      .map((s) => num(s.durationMinutes) || num(s.duration))
      .filter((m) => m > 0))).sort((a, b) => a - b).slice(0, 4);
    const durations: PreviewDuration[] = mins.map((m, i) => ({
      id: "d" + (i + 1), label: durLabel(m), sub: "",
    }));

    return { services, locations, staff, durations };
  }).catch(() => nothing);
}
